import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/** How long to keep waiting for a lazy route's chunk to render the target. */
const RESOLVE_TIMEOUT_MS = 5000;

/**
 * Scroll to `location.hash` once the target actually exists.
 *
 * Every route is code-split behind `Suspense`, so on a cold load of a deep
 * link the browser performs its native anchor jump while only the route
 * fallback is mounted — the element isn't in the DOM yet, the jump silently
 * no-ops, and React then renders the real page at scroll position 0. Polling
 * until the id appears is what makes `/docs/shopify#section` land on the
 * section rather than the top of the page.
 *
 * Honours `scroll-margin-top` (every docs section sets `scroll-mt-24`), so the
 * fixed header doesn't cover the heading. Aborts on any user scroll input:
 * on a slow connection the reader may have started reading from the top, and
 * yanking them away is worse than missing the anchor.
 */
export default function useHashScroll() {
    const { pathname, hash } = useLocation();

    useEffect(() => {
        const id = decodeURIComponent(hash.replace(/^#/, ""));
        if (!id) return;

        let frame = 0;
        let cancelled = false;
        const deadline = Date.now() + RESOLVE_TIMEOUT_MS;

        const abort = () => {
            cancelled = true;
            cancelAnimationFrame(frame);
        };

        const attempt = () => {
            if (cancelled) return;

            const target = document.getElementById(id);
            if (target) {
                target.scrollIntoView({ block: "start" });
                return;
            }
            if (Date.now() < deadline) frame = requestAnimationFrame(attempt);
        };

        // `wheel` and `touchmove` rather than `scroll`, which our own
        // scrollIntoView would otherwise fire and read as user intent.
        window.addEventListener("wheel", abort, { passive: true, once: true });
        window.addEventListener("touchmove", abort, { passive: true, once: true });
        window.addEventListener("keydown", abort, { once: true });
        frame = requestAnimationFrame(attempt);

        return () => {
            abort();
            window.removeEventListener("wheel", abort);
            window.removeEventListener("touchmove", abort);
            window.removeEventListener("keydown", abort);
        };
    }, [pathname, hash]);
}
